import { useEffect, useState } from 'react';
import { CURRENT_VERSION } from './changelog';

/**
 * Polea el endpoint /api/v1/app-version para detectar cuando el server tiene
 * una versión más nueva que la que está corriendo el cliente.
 *
 * Necesario porque después de un deploy, cualquier proxy/cache intermedio
 * (corporate proxy, ISP cache, etc.) puede dejar al usuario corriendo el
 * bundle viejo aunque el server ya tenga uno nuevo. El usuario no se entera
 * hasta que algo rompe o nota un comportamiento raro.
 *
 * Compara semver-version, no buildHash — más simple y suficiente porque
 * cada deploy real del proyecto bumpea CURRENT_VERSION en changelog.ts
 * (regla establecida del proyecto).
 *
 * Cadencia: 60s. Auto-pausa cuando la pestaña está oculta para no consumir
 * requests sin razón. Errores HTTP se ignoran silenciosamente (no toasts —
 * el banner solo aparece cuando hay mismatch confirmado).
 */

const POLL_INTERVAL_MS = 60_000;

interface AppVersionResponse {
  data: {
    version: string;
    builtAt: string | null;
  };
}

interface UseVersionCheck {
  updateAvailable: boolean;
  newVersion: string | null;
}

export function useVersionCheck(): UseVersionCheck {
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch('/api/v1/app-version', {
          credentials: 'omit',
          cache: 'no-store',
        });
        if (!res.ok) return;
        const json = (await res.json()) as AppVersionResponse;
        const v = json?.data?.version;
        if (typeof v === 'string' && v.length > 0 && !cancelled) {
          setServerVersion(v);
        }
      } catch {
        // Silencioso por diseño — un error de red no debe spamear al usuario.
      }
    };

    void check();
    const timer = setInterval(check, POLL_INTERVAL_MS);

    // Re-chequear al volver el foco a la pestaña (caso común: el usuario
    // tenía la pestaña en background mientras se hacía el deploy).
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Si el server reporta una version definida ('unknown' es no-op desde el
  // backend cuando version.json no existe) y difiere de la del cliente, hay
  // update.
  const updateAvailable =
    serverVersion !== null &&
    serverVersion !== 'unknown' &&
    serverVersion !== CURRENT_VERSION;

  return {
    updateAvailable,
    newVersion: updateAvailable ? serverVersion : null,
  };
}
